import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  Minimize2,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
} from "lucide-react";
import {
  TransformWrapper,
  TransformComponent,
  type ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { renderMarkdownWithMath } from "@/lib/markdown-math";
import { cn } from "@/lib/cn";

/**
 * Mind map node as emitted by the Content Factory (NotebookLM `createMindMap`):
 *   { name: string, children?: MindMapNode[] }
 * Detail (formulas, definitions, dates, ...) is emitted as ordinary leaf
 * nodes rather than a special field, so every node renders uniformly.
 * `name` may contain LaTeX (`$...$` / `$$...$$`) and Markdown code (`` `…` ``).
 */
export type MindMapNode = { name: string; children?: MindMapNode[] };

// --------------------------------------------------------------------------
// Layout constants. Horizontal (left-to-right) tidy tree -- reads naturally
// for text labels and pans nicely on mobile.
// --------------------------------------------------------------------------
const NODE_W = 212;
// Minimum card height (short title nodes). Detail leaves carry full
// sentences and grow taller -- their real height is measured at render time
// (see the off-screen measuring layer) and fed into the layout so nothing is
// truncated and siblings never overlap.
const NODE_MIN_H = 56;
const COL_GAP = 288; // x distance between depth columns
const VGAP = 22; // vertical gap between stacked sibling cards
const PAD = 56; // canvas padding around the tree

type FlatNode = {
  id: string;
  name: string;
  depth: number;
  parentId: string | null;
  childIds: string[];
  /** Direct children count -- shown as the "+N" badge when collapsed. */
  childCount: number;
};

type Pos = { x: number; y: number; h: number };

/**
 * Flatten the recursive tree into a Map keyed by a stable path id
 * ("0", "0.1", "0.1.2"). The id doubles as the React key and the
 * expanded-set membership key, so identity survives re-layout.
 */
function flatten(root: MindMapNode): { nodes: Map<string, FlatNode>; rootId: string } {
  const nodes = new Map<string, FlatNode>();
  const build = (node: MindMapNode, id: string, depth: number, parentId: string | null) => {
    const children = node.children ?? [];
    const childIds = children.map((_, i) => `${id}.${i}`);
    nodes.set(id, {
      id,
      name: node.name ?? "",
      depth,
      parentId,
      childIds,
      childCount: children.length,
    });
    children.forEach((child, i) => build(child, childIds[i], depth + 1, id));
  };
  build(root, "0", 0, null);
  return { nodes, rootId: "0" };
}

/**
 * Tidy left-to-right layout over the currently-visible subtree. A node is
 * laid out only if every ancestor is expanded. Leaves (and collapsed
 * branches) take the next vertical slot; expanded branches centre on their
 * children. Each node occupies its own measured height (`heights`, falling
 * back to NODE_MIN_H before measurement), so tall detail cards never overlap
 * their neighbours. `walk` returns a node's vertical *centre*; `pos` stores
 * the card's top-left corner plus its height. Returns absolute positions
 * plus the canvas extent.
 */
function computeLayout(
  nodes: Map<string, FlatNode>,
  rootId: string,
  expanded: Set<string>,
  heights: Map<string, number>,
): { pos: Map<string, Pos>; visible: string[]; width: number; height: number } {
  const pos = new Map<string, Pos>();
  const visible: string[] = [];
  let cursorY = 0;
  let maxBottom = 0;
  let maxX = 0;

  const walk = (id: string): number => {
    const node = nodes.get(id)!;
    visible.push(id);
    const x = node.depth * COL_GAP;
    maxX = Math.max(maxX, x);
    const h = heights.get(id) ?? NODE_MIN_H;
    const open = expanded.has(id) && node.childIds.length > 0;
    let center: number;
    if (open) {
      const childCenters = node.childIds.map((cid) => walk(cid));
      center = (childCenters[0] + childCenters[childCenters.length - 1]) / 2;
    } else {
      center = cursorY + h / 2;
      cursorY += h + VGAP;
    }
    const top = center - h / 2;
    pos.set(id, { x, y: top, h });
    maxBottom = Math.max(maxBottom, top + h);
    return center;
  };
  walk(rootId);

  return {
    pos,
    visible,
    width: maxX + NODE_W + PAD * 2,
    height: maxBottom + PAD * 2,
  };
}

// --------------------------------------------------------------------------
export function MindMapTree({
  data,
  onFullyExplored,
}: {
  data: MindMapNode;
  /** Fired once every branch in the tree has been expanded at least once. */
  onFullyExplored?: () => void;
}) {
  const { nodes, rootId } = useMemo(() => flatten(data), [data]);

  const branchIds = useMemo(
    () => [...nodes.values()].filter((n) => n.childIds.length > 0).map((n) => n.id),
    [nodes],
  );

  const seedExpanded = useCallback(() => {
    // Initial reveal: root + first level open (shows down to depth 2),
    // deeper branches collapsed so the canvas isn't overwhelming.
    const init = new Set<string>();
    nodes.forEach((n) => {
      if (n.childIds.length > 0 && n.depth <= 1) init.add(n.id);
    });
    return init;
  }, [nodes]);

  const [expanded, setExpanded] = useState<Set<string>>(seedExpanded);

  // Re-seed when the tree itself changes (e.g. navigating chapters).
  useEffect(() => {
    setExpanded(seedExpanded());
  }, [seedExpanded]);

  // Every node (visible or not) so heights are measured once up front and the
  // layout never has to wait for a branch to expand.
  const allNodes = useMemo(() => [...nodes.values()], [nodes]);

  // Natural pixel height of each card at NODE_W, captured from the off-screen
  // measuring layer below. Drives the layout so detail cards size to their
  // full text instead of being clamped to a fixed height.
  const measureRefs = useRef(new Map<string, HTMLDivElement>());
  const [heights, setHeights] = useState<Map<string, number>>(new Map());

  useLayoutEffect(() => {
    const next = new Map<string, number>();
    measureRefs.current.forEach((el, id) => {
      next.set(id, Math.max(NODE_MIN_H, Math.ceil(el.getBoundingClientRect().height)));
    });
    setHeights((prev) => {
      if (prev.size === next.size && [...next].every(([k, v]) => prev.get(k) === v)) {
        return prev; // unchanged -- avoid a needless relayout
      }
      return next;
    });
  }, [allNodes]);

  const { pos, visible, width, height } = useMemo(
    () => computeLayout(nodes, rootId, expanded, heights),
    [nodes, rootId, expanded, heights],
  );

  const edges = useMemo(() => {
    const list: { id: string; d: string }[] = [];
    visible.forEach((id) => {
      const n = nodes.get(id)!;
      if (n.parentId == null || !pos.has(n.parentId)) return;
      const p = pos.get(n.parentId)!;
      const c = pos.get(id)!;
      const x1 = p.x + PAD + NODE_W;
      const y1 = p.y + PAD + p.h / 2;
      const x2 = c.x + PAD;
      const y2 = c.y + PAD + c.h / 2;
      const mx = (x1 + x2) / 2;
      list.push({ id, d: `M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}` });
    });
    return list;
  }, [visible, pos, nodes]);

  const ref = useRef<ReactZoomPanPinchRef | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const hasBranches = branchIds.length > 0;
  const allExpanded = hasBranches && branchIds.every((id) => expanded.has(id));

  // "Explored" is cumulative, not a snapshot of `expanded`: collapsing a branch
  // you already read shouldn't un-explore it. Branches at depth <= 1 start open
  // (see seedExpanded), so a shallow tree is explored as soon as it renders --
  // correct, there was nothing further to open.
  const exploredRef = useRef<Set<string>>(new Set());
  const onFullyExploredRef = useRef(onFullyExplored);
  onFullyExploredRef.current = onFullyExplored;
  useEffect(() => {
    expanded.forEach((id) => exploredRef.current.add(id));
    if (hasBranches && branchIds.every((id) => exploredRef.current.has(id))) {
      onFullyExploredRef.current?.();
    }
  }, [expanded, branchIds, hasBranches]);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const fitToView = useCallback((time = 350) => {
    const el = canvasRef.current;
    if (el && ref.current) {
      try {
        ref.current.zoomToElement(el, undefined, time);
      } catch {
        /* element not measurable yet -- ignore */
      }
    }
  }, []);

  // Auto-fit once the first layout has painted, and again after node heights
  // are measured (which reflows the tree).
  useEffect(() => {
    const t = setTimeout(() => fitToView(0), 60);
    return () => clearTimeout(t);
  }, [fitToView, heights]);

  const expandAll = useCallback(() => {
    setExpanded(new Set(branchIds));
    setTimeout(() => fitToView(420), 60);
  }, [branchIds, fitToView]);

  const collapseAll = useCallback(() => {
    setExpanded(new Set());
    setTimeout(() => fitToView(420), 60);
  }, [fitToView]);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      setFullscreen(false);
    } else {
      void el.requestFullscreen();
      setFullscreen(true);
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        // Definite height (not min-height) so the pan/zoom surface is locked
        // to the viewport: overflow-hidden clips the canvas and panning -- not
        // the page -- reveals the rest of the tree.
        "relative w-full overflow-hidden bg-surface-2 dark:bg-bg",
        fullscreen ? "h-screen" : "h-[calc(100vh-9rem)]",
      )}
    >
      {/* Off-screen measuring layer: renders every card at its natural height
          so the layout can size detail nodes to their full text. Invisible and
          out of flow -- never painted, only measured. */}
      <div
        aria-hidden
        className="invisible absolute -left-[9999px] top-0"
        style={{ width: NODE_W }}
      >
        {allNodes.map((n) => (
          <div
            key={n.id}
            ref={(el) => {
              if (el) measureRefs.current.set(n.id, el);
              else measureRefs.current.delete(n.id);
            }}
            style={{ width: NODE_W }}
          >
            <NodeCard
              name={n.name}
              depth={n.depth}
              isBranch={n.childIds.length > 0}
              isOpen={false}
              childCount={n.childCount}
              onToggle={() => {}}
              measuring
            />
          </div>
        ))}
      </div>

      <TransformWrapper
        ref={ref}
        initialScale={1}
        minScale={0.2}
        maxScale={2.5}
        wheel={{ step: 0.08, smoothStep: 0.0035 }}
        pinch={{ step: 5 }}
        panning={{ velocityDisabled: false }}
        doubleClick={{ disabled: true }}
        limitToBounds={false}
        centerZoomedOut
      >
        <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-max">
          <div ref={canvasRef} className="relative" style={{ width, height }}>
            {/* Connectors ------------------------------------------------ */}
            <svg
              width={width}
              height={height}
              className="absolute inset-0 pointer-events-none"
              style={{ overflow: "visible" }}
            >
              <AnimatePresence>
                {edges.map((e) => (
                  <motion.path
                    key={e.id}
                    fill="none"
                    stroke="rgb(var(--border-strong))"
                    strokeWidth={2}
                    strokeLinecap="round"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, d: e.d }}
                    exit={{ opacity: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 28 }}
                  />
                ))}
              </AnimatePresence>
            </svg>

            {/* Nodes ----------------------------------------------------- */}
            <AnimatePresence>
              {visible.map((id) => {
                const n = nodes.get(id)!;
                const p = pos.get(id)!;
                const isBranch = n.childIds.length > 0;
                const isOpen = isBranch && expanded.has(id);
                return (
                  <motion.div
                    key={id}
                    className="absolute top-0 left-0"
                    style={{ width: NODE_W }}
                    initial={{ opacity: 0, scale: 0.9, x: p.x + PAD, y: p.y + PAD }}
                    animate={{ opacity: 1, scale: 1, x: p.x + PAD, y: p.y + PAD }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 240, damping: 28 }}
                  >
                    <NodeCard
                      name={n.name}
                      depth={n.depth}
                      isBranch={isBranch}
                      isOpen={isOpen}
                      childCount={n.childCount}
                      onToggle={() => toggle(id)}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </TransformComponent>

        {/* Expand / collapse-all pill -- top-left. */}
        {hasBranches ? (
          <div className="absolute z-10 top-3 left-3 sm:top-4 sm:left-4 flex items-center gap-1 p-1 rounded-full border border-border bg-surface/95 backdrop-blur-md shadow-md">
            <Button
              variant="ghost"
              size="sm"
              onClick={allExpanded ? collapseAll : expandAll}
              className="h-9 rounded-full px-3 text-xs"
              aria-label={allExpanded ? "Collapse all branches" : "Expand all branches"}
            >
              {allExpanded ? (
                <ChevronsDownUp className="h-4 w-4 sm:mr-1.5" />
              ) : (
                <ChevronsUpDown className="h-4 w-4 sm:mr-1.5" />
              )}
              <span className="hidden sm:inline">{allExpanded ? "Collapse all" : "Expand all"}</span>
            </Button>
          </div>
        ) : null}

        {/* Zoom + view controls -- top-right. */}
        <div className="absolute z-10 top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-1 p-1 rounded-full border border-border bg-surface/95 backdrop-blur-md shadow-md">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Zoom out"
            onClick={() => ref.current?.zoomOut()}
            className="h-9 w-9 rounded-full"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Fit to view"
            onClick={() => fitToView(350)}
            className="h-9 w-9 rounded-full"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Zoom in"
            onClick={() => ref.current?.zoomIn()}
            className="h-9 w-9 rounded-full"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <span className="w-px h-5 bg-border mx-0.5" />
          <Button
            variant="ghost"
            size="icon"
            aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
            onClick={toggleFullscreen}
            className="h-9 w-9 rounded-full"
          >
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>

        <p className="absolute z-10 bottom-3 left-1/2 -translate-x-1/2 text-[11px] sm:text-xs text-text-subtle bg-surface/90 backdrop-blur px-3 py-1 rounded-full border border-border whitespace-nowrap">
          <span className="sm:hidden">Tap a node to expand • pinch to zoom • drag to pan</span>
          <span className="hidden sm:inline">
            Click a node to expand / collapse • scroll to zoom • drag to pan
          </span>
        </p>
      </TransformWrapper>
    </div>
  );
}

// --------------------------------------------------------------------------
const ACCENT_ALPHA = [1, 0.7, 0.5, 0.4];

function NodeCard({
  name,
  depth,
  isBranch,
  isOpen,
  childCount,
  onToggle,
  measuring = false,
}: {
  name: string;
  depth: number;
  isBranch: boolean;
  isOpen: boolean;
  childCount: number;
  onToggle: () => void;
  /** When true the card renders as a static div for off-screen measurement. */
  measuring?: boolean;
}) {
  // `name` may carry LaTeX / Markdown code -- render through the shared pipeline.
  const nameHtml = useMemo(() => renderMarkdownWithMath(name), [name]);

  const base =
    "block w-full rounded-xl border px-3.5 py-3 shadow-sm transition-[filter,transform] duration-150";
  const tone =
    depth === 0
      ? "bg-brand text-white border-transparent shadow-md"
      : depth === 1
        ? "bg-brand-soft text-brand border-brand/30"
        : "bg-surface text-text border-border";
  const accent = depth === 0 ? "bg-white/20 text-white" : "bg-brand/15 text-brand";

  // Natural height: at least NODE_MIN_H, growing to fit wrapped text. The
  // measured value feeds the layout so nothing overlaps or gets truncated.
  const style: React.CSSProperties = { minHeight: NODE_MIN_H };
  if (depth >= 2) {
    const alpha = ACCENT_ALPHA[Math.min(depth - 2, ACCENT_ALPHA.length - 1)];
    style.borderLeftWidth = 3;
    style.borderLeftStyle = "solid";
    style.borderLeftColor = `rgb(var(--brand) / ${alpha})`;
  }

  const inner = (
    <div className="flex h-full items-center gap-2">
      <span
        className={cn(
          // Detail leaves can be a full sentence -- the card grows to fit the
          // whole thing (no clamping), so wrap freely. Inline code renders
          // monospace via the default <code> styling from the markdown pipeline.
          "flex-1 text-[13px] leading-snug break-words",
          "[&_p]:m-0 [&_p]:inline [&_.katex-display]:inline",
          "[&_code]:rounded [&_code]:bg-black/5 dark:[&_code]:bg-white/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em]",
          depth === 0 ? "font-semibold" : "font-medium",
        )}
        title={name}
        dangerouslySetInnerHTML={{ __html: nameHtml }}
      />
      {isBranch ? (
        isOpen ? (
          <ChevronRight className="h-4 w-4 shrink-0 rotate-90 opacity-70" />
        ) : (
          <span
            className={cn(
              "shrink-0 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums",
              accent,
            )}
          >
            {childCount}
          </span>
        )
      ) : null}
    </div>
  );

  if (measuring || !isBranch) {
    return (
      <div className={cn(base, tone, "cursor-default")} style={style}>
        {inner}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-label={isOpen ? `Collapse ${name}` : `Expand ${name}, ${childCount} hidden`}
      className={cn(base, tone, "text-left cursor-pointer hover:brightness-[1.04] active:scale-[0.98]")}
      style={style}
    >
      {inner}
    </button>
  );
}
